import React, { type JSX, useEffect, useMemo } from 'react';
import { Icon, Loader } from '@saviynt/design-system';
import classNames from 'classnames';

import { RequestStatus } from '../../../models';
import { type MenuId } from '../../../models';
import {
  findAccessibleMenuLinks,
  type MenuLink,
  type SideMenuNode,
  useCookies,
  useInvertedLocalize,
} from '../../../utilities';
import { type RootState, thunks, useDispatch, useSelector } from '../store';

import { invertedMessages } from './messages';
import ShortcutTile from './ShortcutTile';

import './ShortcutsWidget.css';

interface SelectedState {
  defaultShortcutIds?: MenuId[] | 'error';
  defaultShortcutsStatus: RequestStatus;
  gatewayApiUrl: string;
  sideMenu: SideMenuNode[] | null;
  menuList: MenuLink[] | undefined;
}

export interface ShortcutsWidgetProps {
  onCustomClick(id: string): void;
  className?: string;
}

ShortcutsWidget.defaultProps = {
  className: undefined,
};

export default function ShortcutsWidget({
  onCustomClick,
  ...props
}: ShortcutsWidgetProps): JSX.Element | null {
  const l = useInvertedLocalize(invertedMessages);
  const [{ userKey }] = useCookies('userKey');
  const dispatch = useDispatch();
  const {
    defaultShortcutIds,
    defaultShortcutsStatus,
    gatewayApiUrl,
    sideMenu,
    menuList,
  } = useSelector(selectState);
  const shortcuts = useMemo(
    () =>
      findAccessibleMenuLinks({
        userKey,
        sideMenu,
        menuList,
        menuIds: defaultShortcutIds,
      }),
    [defaultShortcutIds, sideMenu, menuList, userKey]
  );

  useEffect(() => {
    if (gatewayApiUrl && defaultShortcutIds === undefined) {
      dispatch(thunks.requestShortcuts(gatewayApiUrl));
    }
  }, [dispatch, gatewayApiUrl, defaultShortcutIds]);

  if (defaultShortcutIds === 'error') {
    const errorClassName = classNames(
      'Nav_ShortcutsWidget',
      props.className,
      'Nav_ShortcutsWidget--error'
    );

    return (
      <div className={errorClassName}>
        <Icon
          kind='AlertCriticalOutline'
          color='critical-700'
          className='Nav_ShortcutsWidget-errorIcon'
        />{' '}
        <span className='Nav_ShortcutsWidget-errorMessage'>
          {l(
            'There was a problem loading your shortcuts. Refresh the page to retry.'
          )}
        </span>
      </div>
    );
  }

  if (defaultShortcutsStatus !== RequestStatus.Idle || shortcuts === null) {
    const loadingClassName = classNames(
      'Nav_ShortcutsWidget',
      props.className,
      'Nav_ShortcutsWidget--loading'
    );

    return (
      <div className={loadingClassName}>
        <Loader kind='dots' format='inline' color='brand' size='small' />
      </div>
    );
  }

  const className = classNames('Nav_ShortcutsWidget', props.className);

  return (
    <div className={className}>
      {shortcuts.map((shortcut) => (
        <ShortcutTile
          className='Nav_ShortcutsWidget-tile'
          key={shortcut.id}
          shortcut={shortcut}
          onCustomClick={onCustomClick}
        />
      ))}
    </div>
  );
}

function selectState(state: RootState): SelectedState {
  const {
    shortcuts: { defaultShortcutIds, defaultShortcutsStatus },
    user: {
      domainInfo: { GATEWAY_API_URL: gatewayApiUrl },
    },
    app: { sideMenu },
    profile: { menuList },
  } = state;

  return {
    defaultShortcutsStatus,
    defaultShortcutIds,
    gatewayApiUrl,
    sideMenu,
    menuList,
  };
}
